import type { ButtonProps, ButtonVariants } from "@qws-org/ui-kit";
import { Button, Flex, Image } from "@qws-org/ui-kit";
import type { FC } from "react";

import type { Action, Pages } from "~/core";
import {
  isExternalLink,
  isInternalLink,
  isTemplateUrl,
} from "~/core/utils/action.utils";
import type { HeaderData } from "~/features/headerV4/HeaderV4";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

export type HeaderButtonProps = {
  item: NonNullable<
    ExtractArrayItemType<
      NonNullable<
        NonNullable<NonNullable<HeaderData>["additional_menu"]>["action_items"]
      >
    >
  >["menu_action_items_id"];
  getUrl: (page: Pages) => string;
  onCallAction: (action: Action) => void;
  variant?: ButtonVariants;
} & ButtonProps<"button">;

export const HeaderButton: FC<HeaderButtonProps> = ({
  item,
  getUrl,
  onCallAction,
  ...props
}) => {
  const controlAction = item?.action;
  const action = controlAction?.action?.[0];
  let href: string | undefined;
  if (isExternalLink(action?.item)) {
    if (isTemplateUrl(action?.item)) {
      href = undefined;
    } else {
      href = action.item.href ?? undefined;
    }
  }
  if (isInternalLink(action?.item)) {
    href = action.item.page ? getUrl(action.item.page) : undefined;
  }

  const { getDirectusImage } = useDirectus();
  const icon = item?.translations?.[0]?.icon;
  const mIconId = icon?.image?.id ?? "";
  const mdIconId = icon?.tablet_image?.id ?? mIconId;
  const dIconId = icon?.desktop_image?.id ?? mdIconId;
  const mWidth = icon?.mobile_image_width ?? undefined;
  const mHeight = icon?.mobile_image_height ?? undefined;
  const mdWidth = icon?.tablet_image_width ?? mWidth;
  const mdHeight = icon?.tablet_image_height ?? mHeight;
  const dWidth = icon?.desktop_image_width ?? mdWidth;
  const dHeight = icon?.desktop_image_height ?? mdHeight;
  return (
    <Button
      p={{ px: 12, py: 8 }}
      href={href}
      display={
        item?.action?.inactive_if?.includes("isMobile")
          ? "hidden"
          : item?.action?.inactive_if?.includes("isDesktop")
            ? "block"
            : undefined
      }
      width="100%"
      className={`whitespace-nowrap ${props.className ?? ""}`}
      onPress={() => {
        if (controlAction) {
          onCallAction(controlAction);
        }
      }}
      size="small"
      fontWeight="mobile.bold"
      {...props}
      md={{
        p: { px: 24, py: 12 },
        display: item?.action?.inactive_if?.includes("isMobile")
          ? "block"
          : item?.action?.inactive_if?.includes("isDesktop")
            ? "hidden"
            : undefined,
        ...props.md,
      }}
    >
      <Flex align="center" justify="center" gap={12}>
        {icon && (
          <Image
            value={getDirectusImage(mIconId, {
              width: mWidth,
              height: mHeight,
            })}
            mdValue={getDirectusImage(mdIconId, {
              width: mdWidth,
              height: mdHeight,
            })}
            dValue={getDirectusImage(dIconId, {
              width: dWidth,
              height: dHeight,
            })}
            width={mWidth}
            height={mHeight}
            md={{ width: mdWidth, height: mdHeight }}
            d={{ width: dWidth, height: dHeight }}
          />
        )}
        {item?.translations?.[0]?.name}
      </Flex>
    </Button>
  );
};
